import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { AppHeader } from "@/components/AppHeader";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, ClipboardList, BookMarked, Presentation, Code2, FileText } from "lucide-react";

const SUBJECT_META = {
  computer: { name: "Computer", color: "hsl(200 90% 50%)" },
  english: { name: "English", color: "hsl(10 85% 62%)" },
  mathematics: { name: "Mathematics", color: "hsl(260 70% 60%)" },
};

export default function TopicsPage() {
  const { subjectId, classId } = useParams();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingChat, setStartingChat] = useState(null);
  const subject = SUBJECT_META[subjectId] || SUBJECT_META.computer;

  useEffect(() => {
    api
      .get(`/syllabus/${subjectId}/${classId}`)
      .then((r) => setTopics(r.data.topics))
      .catch(() => toast.error("Failed to load chapters"))
      .finally(() => setLoading(false));
  }, [subjectId, classId]);

  const startChat = async (topicId) => {
    setStartingChat(topicId);
    try {
      const { data } = await api.post("/chat/start", { topic_id: topicId });
      navigate(`/tutor/${data.session_id}`);
    } catch (e) {
      if (!e.isSubscriptionError)
        toast.error(e?.response?.data?.detail || "Failed to start chat");
    } finally {
      setStartingChat(null);
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <Link
          to={`/subjects/${subjectId}/classes`}
          data-testid="back-to-classes"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to classes
        </Link>

        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div
              className="text-sm font-bold uppercase tracking-widest mb-3"
              style={{ color: subject.color }}
            >
              {subject.name} · Class {classId}
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2">
              Chapters
            </h1>
            <p className="text-muted-foreground">
              Pick a chapter to chat with your AI tutor or take a quick quiz.
            </p>
          </div>
          {topics.length > 0 && (
            <div className="text-sm font-bold text-muted-foreground">
              {topics.length} chapters
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading chapters…</div>
        ) : (
          <div className="space-y-3">
            {topics.map((t, idx) => (
              <div
                key={t.id}
                className="tactile-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`topic-card-${t.id}`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 font-black text-lg"
                    style={{
                      background: `${subject.color}15`,
                      color: subject.color,
                    }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">
                      <BookMarked className="w-3 h-3" /> Chapter {idx + 1}
                      {t.category && (
                        <span
                          data-testid={`topic-category-${t.id}`}
                          className="ml-1 px-2 py-0.5 rounded-full text-[10px] tracking-wide"
                          style={{
                            background: `${subject.color}20`,
                            color: subject.color,
                          }}
                        >
                          {t.category}
                        </span>
                      )}
                    </div>
                    <div className="text-lg sm:text-xl font-bold leading-snug">
                      {t.title}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button
                    data-testid={`chat-btn-${t.id}`}
                    onClick={() => startChat(t.id)}
                    disabled={startingChat === t.id}
                    className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white text-sm px-4 py-2.5 disabled:opacity-50"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {startingChat === t.id ? "Opening…" : "Chat"}
                  </button>
                  <Link
                    data-testid={`notes-btn-${t.id}`}
                    to={`/materials/${t.id}`}
                    className="pill-btn bg-[hsl(var(--brimi-mint))] hover:bg-[hsl(var(--brimi-mint))]/90 text-white text-sm px-4 py-2.5"
                  >
                    <Presentation className="w-4 h-4" /> Notes
                  </Link>
                  <Link
                    data-testid={`quiz-btn-${t.id}`}
                    to={`/quiz/${t.id}`}
                    className="pill-btn bg-[hsl(var(--brimi-coral))] hover:bg-[hsl(var(--brimi-coral))]/90 text-white text-sm px-4 py-2.5"
                  >
                    <ClipboardList className="w-4 h-4" /> Quiz
                  </Link>
                  {t.has_challenges && (
                    <Link
                      data-testid={`challenges-btn-${t.id}`}
                      to={`/challenges/${t.id}`}
                      className="pill-btn bg-[hsl(var(--brimi-yellow))] hover:bg-[hsl(var(--brimi-yellow))]/90 text-foreground text-sm px-4 py-2.5"
                    >
                      <Code2 className="w-4 h-4" /> Challenges
                    </Link>
                  )}
                  <Link
                    data-testid={`papers-btn-${t.id}`}
                    to={`/papers/${t.id}`}
                    className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-foreground text-sm px-4 py-2.5"
                  >
                    <FileText className="w-4 h-4" /> Papers
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
